import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Grid, Typography, Button, Divider, Link, ListItem, List } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { data, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import DecisionNode from "./DecisionNode";
import DecisionLog from "./DecisionLog";
import DecisionLog2 from "./DecisionLog2";
import Stepper from "./Stepper";
import { styled } from "@mui/material/styles";
import DecisionTree from "./DecisionTreeApi";
import DecisionTree2 from "./DecisionTree2";
import { gql, useLazyQuery } from "@apollo/client";
import { useSelector } from "react-redux";
import { ACCELIDEA_DARK_GREEN, ACCELIDEA_MID_GREEN } from "../../styles/colors";
import { GET_STANDARD_DECISIONS } from "../../gql/queries/standardDecisions";

const DecisionTreeWizard = () => {
  // console.log("Rendering DecisionTreeWizard component");

  // Define your tile options (could be fetched or static)
  const treeOptions = [
    { id: 1, label: "ISO 10993-1:2018", description: "Biological Evaluation of Medical Devices", standardId: "iso10993" },
    { id: 2, label: "IEC 60601-1:2005", description: "Medical Electrical Equipment — General requirements for basic safety and essential performance", standardId: "iec60601" },
  ];
  const firstStandard = treeOptions[0].standardId;

  const navigate = useNavigate();
  const ideaId = useSelector((state) => state?.explore?.idea?.id);

  const [decisionData, setDecisionData] = useState({});
  const [decisionData2, setDecisionData2] = useState({});
  const [activeRightTab, setActiveRightTab] = useState("DecisionLog"); // "HelpDecide" or "DecisionLog"
  const [helpText, setHelpText] = useState("");

  const transitionConfig = { duration: 0.17, ease: "easeInOut" };
  const [currentDecision, setCurrentDecision] = useState(1);
  const [selectedOption, setSelectedOption] = useState(null); // Track selected Yes/No
  const [topResults, setTopResults] = useState({}); // Store results for all decisions
  const [totalDecisions, setTotalDecisions] = useState(0);
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [decisionMade, setDecisionMade] = useState(false); // Track if a decision was made
  const [resultText, setResultText] = useState(null);
  const [isResultTextActive, setIsResultTextActive] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [resultBoxPosition, setResultBoxPosition] = useState({ x: 0, y: 0 });
  const [lineConnection, setLineConnection] = useState(null);
  const [isRootSelected, setIsRootSelected] = useState(false);
  const [isRootGray, setIsRootGray] = useState(true);
  const [decisionHistory, setDecisionHistory] = useState({});
  const [marginTop, setMarginTop] = useState(80);  // State to control marginTop dynamically

  const [isComplete, setIsComplete] = useState(false);

  const mapCommittedDecisions = (data) => {
    // console.log("Mapping committed decisions from data:", data);
    
    // Check if the data object and necessary nested properties exist
    const committedDecision = data?.idea?.committedDecisions[currentDecision - 2];

    if (!committedDecision) {
      return {}; // Return an empty object if the data structure is invalid
    }

    return {
      rationale: committedDecision.decision?.description || "Pending",
      verified: committedDecision.option?.description || "Pending",
      usedIn: committedDecision.option?.label || "Pending"
    };
  }

  const GET_DECISION_LOG_GQL = gql`
  query GetDecisionLog ($ideaId: ID!){
  idea(id: $ideaId) {
    committedDecisions {
      id
      decision {
        id
        description
      }
      option {
        id
        label
        description
      }
      committedAt
    }
  }
}
  `;

  const [fetchDecisionLog] = useLazyQuery(
    GET_DECISION_LOG_GQL,
    {
      fetchPolicy: 'no-cache'
    }
  )

  const updateDecisionLog = () => {
    fetchDecisionLog({
      variables: {
        ideaId: ideaId
      }
    })
      .then((res) => {
        setDecisionData({
          ...decisionData,
          [currentDecision - 1]: mapCommittedDecisions(res?.data)
        })
      })
  }

  useEffect(() => {
    updateDecisionLog();
  }, [currentDecision])

  const handleHelpClick = () => {
    setShowSuggestion((prev) => !prev); // Toggle the state
  };

  const saveCurrentDecisionState = () => {
    setDecisionHistory((prev) => ({
      ...prev,
      [currentDecision]: {
        resultText,
        selectedOption,
        selectedChild,
        resultBoxPosition,
        lineConnection,
        isResultTextActive,
      },
    }));
  };

  useEffect(() => {
    if (currentDecision > 1) {
      saveCurrentDecisionState();
    }
  }, [currentDecision]);

  // Back button logic
  const handleBackClick = () => {
    if (currentDecision > 1) {
      // Step 1: Decrease the current decision level
      const newDecision = currentDecision - 1;
      setCurrentDecision(newDecision);
      setIsComplete(false);

      // Step 2: Retrieve the previous state from the history
      const previousState = decisionHistory[newDecision];

      if (previousState) {
        // Step 3: Restore the state from the previous decision
        setResultText(previousState.resultText || null);
        setSelectedOption(previousState.selectedOption || null);
        setSelectedChild(previousState.selectedChild || null);
        setResultBoxPosition(previousState.resultBoxPosition || { x: 0, y: 0 });
        setLineConnection(previousState.lineConnection || null);
        setIsResultTextActive(false); // Reset the active state for resultText
      } else {
        // Step 4: Reset the state if no history is found for the previous decision
        setResultText(null);
        setSelectedOption(null);
        setSelectedChild(null);
        setResultBoxPosition({ x: 0, y: 0 });
        setLineConnection(null);
        setIsResultTextActive(false);
      }

      // Step 5: Reset root selection state for the previous decision
      setIsRootSelected(false);

      // Step 6: Adjust the isRootGray state based on the new decision level
      setIsRootGray(newDecision > 1); // Make root gray for all decisions after the first
      if (isComplete && selectedChild) {
        setMarginTop(80);
      } else if (newDecision === 1) {
        setMarginTop(80);
      } else {
        setMarginTop(220);
      }
    }
  };

  useEffect(() => {
    // Ensure marginTop is correctly set for the last decision
    if (isComplete && selectedChild) {
      setMarginTop(80); // Last decision, selected a child
    } else if (currentDecision === 1) {
      setMarginTop(80); // First decision
    } else {
      setMarginTop(220); // Any other decision
    }
  }, [currentDecision, totalDecisions, selectedChild, isComplete]);

  const [getStandardDecisions] = useLazyQuery(GET_STANDARD_DECISIONS, {
    fetchPolicy: "no-cache",
    variables: { standardId: firstStandard, ideaId: "cmg2ovtdf0000ajdk9bnqn652", userId: "user_32vISRGYnuVrL6kntJtzS2UoYJn" },
  });

  // Pull standard decisions data into memory
  useEffect(() => {
    // console.log("useEffect for fetching standard decisions for decision tree");
    getStandardDecisions().then((res) => {
        // console.log("Fetched Standard Decisions:", res);
        // Transform the data into a more accessible format
        const mappedObject = {};
        res.data.getStandardDecisions.forEach((decision, index) => {
        mappedObject[index + 1] = {
            id: decision.id,
            step: decision.step,
            question: decision.question,
            help: decision.help,
            options: decision.options,
            totalDecisions: res.data.getStandardDecisions.length,
            currentDecision: 1,
        };
        });

        // console.log("Mapped Standard Decisions:", mappedObject);
        
        setDecisionData2(mappedObject);
        // setCurrentData(mappedObject[currentDecision]);
    });
  }, [getStandardDecisions, currentDecision]);
  
  useEffect(() => {
    // Save the state when currentDecision changes
    setDecisionHistory((prev) => ({
      ...prev,
      [currentDecision]: { resultText, isResultTextActive },
    }));
  }, [currentDecision, resultText, isResultTextActive, isComplete]);

  const handleTotalDecisionsChange = (newTotalDecisions) => {
    setTotalDecisions(newTotalDecisions); // Update the state with the count of decisions
  };

  // Add state to track which tree is selected
  const [selectedTree, setSelectedTree] = useState(null); // Use the 1-based id
  const [selectedTreeIndex, setSelectedTreeIndex] = useState(null); // Zero-based index

  // Handler for tile click
  const handleTileClick = (treeId) => {
    setSelectedTree(treeId);
    const treeIndex = treeId && treeId > 0 ? treeId - 1 : null
    console.log("Tree index on tile click:", treeIndex);
    setSelectedTreeIndex(treeIndex);
    setCurrentDecision(1); // Reset to first decision
    setDecisionData({});
    setDecisionHistory({});
    setSelectedOption(null);
    setSelectedChild(null);
    setResultText(null);
    setIsComplete(false);
    setIsRootSelected(false);
    setIsRootGray(true);
    setMarginTop(80);
  };

  // Handler for going back to tile selection
  const handleBackToTiles = () => {
    setSelectedTree(null);
    setSelectedTreeIndex(null);
    setCurrentDecision(1);
    setDecisionData({});
    setDecisionHistory({});
    setSelectedOption(null);
    setSelectedChild(null);
    setResultText(null);
    setIsComplete(false);
    setIsRootSelected(false);
    setIsRootGray(true);
    setMarginTop(80);
  };

  // Show tile selection screen if no tree is selected
  if (!selectedTree) {
    return (
      <WizardContainer>
      {/* Sticky header */}
      <Header
        style={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        background: "#fff",
        paddingBottom: "16px",
        }}
      >
        <WizardTitle>
        Select a standard to go through its decision tree
        </WizardTitle>
      </Header>
      <Box
        sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "flex-start",
        gap: 4,
        marginTop: "4rem",
        flexWrap: "wrap",
        }}
      >
        {treeOptions.map((tree) => {
        // Calculate progress percentage for each tree
        const isSelected = selectedTree === tree.id;
        // const progress =
        //   decisionData && decisionData[tree.id] && decisionData[tree.id].totalDecisions
        //   ? Math.round(
        //     ((decisionData[tree.id].currentDecision || 0) /
        //       decisionData[tree.id].totalDecisions) *
        //       100
        //     )
        //   : 0;

        // Set defaults
        let decisionCount = 0;
        let completedDecisionCount = 0;
        let progress = 0;

        if(tree.standardId === firstStandard) {
          // Get a count of the total decisions for this tree
          decisionCount = decisionData2 ? Object.keys(decisionData2).length : 0;
          //console.log("Decision count for tree:", decisionCount);
          // Get a count of the decisions made for this tree based on the options marked as chosen
          completedDecisionCount = decisionData2
            ? Object.values(decisionData2).filter(decision => decision.options.some(opt => opt.chosen)).length
            : 0;
          //console.log("Completed decision count for tree:", completedDecisionCount);
          progress =
            decisionCount > 0
            ? Math.round(
              (completedDecisionCount /
                decisionCount) *
                100
              )
            : 0;
          //console.log(`Progress for tree ${tree.id}:`, progress);
        }


        return (
          <Box
          key={tree.id}
          onClick={() => handleTileClick(tree.id)}
          sx={{
          cursor: "pointer",
          background: "#fff",
          color: "#000",
          borderRadius: "12px",
          border: "2px solid #e0e0e0",
          boxShadow: 2,
          p: 4,
          textAlign: "left",
          transition: "0.2s",
          width: "420px",
          maxWidth: "40vw",
          "&:hover": { borderColor: "#bdbdbd" },
          marginBottom: "1.5rem",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          position: "relative",
          minHeight: "132px", // 60% of 220px
          height: "132px",    // Fixed height for all boxes
          justifyContent: "space-between", // Ensure content is spaced
          }}
          >
          <Typography variant="h6" fontWeight={700} color="#000" align="left">
          {tree.label}
          </Typography>
          <Typography variant="body2" mt={2} color="#303030" align="left">
          {tree.description}
          </Typography>
          <Box sx={{ width: "100%", mt: "auto", display: "flex", alignItems: "center", gap: 2, justifyContent: "space-between" }}>
          <Box
            sx={{
              height: "8px",
              background: "#e0e0e0",
              borderRadius: "4px",
              overflow: "hidden",
              width: "40%",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${progress}%`,
                background: "#038C5A",
                transition: "width 0.3s",
              }}
            />
          </Box>
          <Typography variant="caption" color="#303030" sx={{ minWidth: "32px", textAlign: "right" }}>
            {progress}%
          </Typography>
          <Link
            component="button"
            variant="body2"
            sx={{
              color: "#038C5A",
              fontWeight: 700,
              textDecoration: "underline",
              fontSize: "16px",
              cursor: "pointer",
              marginLeft: "auto",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleTileClick(tree.id);
            }}
          >
            Start Wizard
          </Link>
          </Box>
          </Box>
          );
        })}
        </Box>
      </WizardContainer>
    );
  }

  // Render the wizard as before, with a back button to tile selection
  // Dynamically import the decision tree data file based on selectedTree
  const selectedTreeObj = treeOptions.find(t => t.id === selectedTree);
  // const treeData = selectedTreeObj
  //   ? require(`./Data/${selectedTreeObj.dataFile}.js`).default
  //   : null;

  // const standardIdCheck = selectedTreeIndex >= 0 && treeOptions[selectedTreeIndex] ? treeOptions[selectedTreeIndex].standardId : null;
  // console.log("selectedTreeIndex:", selectedTreeIndex);
  // console.log("treeOptions[selectedTreeIndex]:", treeOptions[selectedTreeIndex]);
  // console.log("Standard ID Check:", standardIdCheck);

  return (
    <WizardContainer>
      <Header>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              borderRadius: "50%",
              transition: "background 0.2s",
              "&:hover": { background: "#e0f2ea" },
              p: "4px",
              mr: "8px",
              height: "30px",
            }}
            onClick={handleBackToTiles}
            tabIndex={0}
            role="button"
            aria-label="Back to tile selection"
          >
            <ArrowBackIcon sx={{ color: "#038c5a", fontSize: "32px", margin: "8px" }} />
          </Box>
          <Box sx={{ height: "48px" }}>
            <WizardTitle>
              {selectedTreeObj?.label || "Decision Tree Wizard"}
            </WizardTitle>
          </Box>
        </Box>
      </Header>

      {/* Tab content */}
      <WizardContent container spacing={2}>
        <Grid
          style={{
            width: "100%",
            display: "grid",
            gap: "3.5rem",
            padding: "0.4rem",
          }}
          item
          xs={12}
          md={8}
        >
          <MainColumn>
            <TreeSection>
              <DecisionTree2
                standardId={selectedTreeIndex >= 0 && treeOptions[selectedTreeIndex] ? treeOptions[selectedTreeIndex].standardId : null}
                decisionData={decisionData2}
                currentDecision={currentDecision}
                setCurrentDecision={setCurrentDecision}
                setDecisionData={setDecisionData2}
                setActiveRightTab={setActiveRightTab}
              />
            </TreeSection>
          </MainColumn>

          {selectedChild && isComplete && (
            <ResultBoxContainer>
              <Title>
                Congratulations -{" "}
                <span style={{ color: "#000" }}>
                  You have finished the wizard for {selectedTreeObj?.label}
                </span>
              </Title>
              <Subtitle>Automatic Updates by Accelidea:</Subtitle>
              <StyledList>
                <StyledListItem>
                  ISO 10993-5 and 10993-10 were added to{" "}
                  <StyledLink>Standards list</StyledLink>
                </StyledListItem>
                <StyledListItem>
                  <StyledLink>
                    Glossary terms for Product Management
                  </StyledLink>{" "}
                  were updated
                </StyledListItem>
                <StyledListItem>
                  <StyledLink>
                    Glossary terms for Engineering
                  </StyledLink>{" "}
                  were updated
                </StyledListItem>
              </StyledList>
            </ResultBoxContainer>
          )}
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Divider
            orientation="vertical"
            flexItem
            style={{
              height: "80vh",
              borderRight: "2px solid #000",
              paddingLeft: "1.2rem",
            }}
          />
        </Grid>

        <Grid style={{ width: "100%" }} item xs={12} md={8}>
          <SideColumn>
            {/* Tabs */}
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button
                variant={activeRightTab === "DecisionLog" ? "contained" : "outlined"}
                color="success"
                onClick={() => setActiveRightTab("DecisionLog")}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Decision Log
              </Button>
              <Button
                variant={activeRightTab === "HelpDecide" ? "contained" : "outlined"}
                color="success"
                onClick={() => setActiveRightTab("HelpDecide")}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Help Decide
              </Button>
            </Box>

            {activeRightTab === "DecisionLog" && (
              <Box sx={{ p: 4 }}>
                <DecisionLog2
                  decisionData={decisionData2}
                  currentDecision={currentDecision}
                  setCurrentDecision={setCurrentDecision}
                />
              </Box>
            )}

            {activeRightTab === "HelpDecide" && (
              <Box sx={{ p: 4 }}>
                {/* Replace with your HelpDecide content/component */}
                {/* <Typography variant="h6" mb={2}>
                  Help Decide
                </Typography> */}
                <Typography variant="body1">
                  {decisionData2 && decisionData2[currentDecision] && decisionData2[currentDecision].help
                    ? decisionData2[currentDecision].help
                    : "Help content is not available for this question."}
                </Typography>
              </Box>
            )}

            {isResultTextActive && (
              <StyledButton onClick={() => navigate("/Tracer-Requirements")}>
                <ButtonText>Finish and Go to Summary</ButtonText>
              </StyledButton>
            )}
          </SideColumn>
        </Grid>
      </WizardContent>

    </WizardContainer>
  );
};

const StyledButton = styled(Button)(({ theme }) => ({
  boxSizing: 'border-box',

  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',

  padding: '10px 20px',
  gap: '20px',
  width: 'max-content',

  height: '44px',

  border: '1px solid #303030',
  borderRadius: '4px',

  '&:hover': {
    backgroundColor: '#f5f5f5', // Adjust for hover effect
  }
}));
const ButtonText = styled(Typography)(({ theme }) => ({


  fontFamily: 'Inter',
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '20px',
  lineHeight: '24px',
  fontFeatureSettings: "'tnum' on, 'lnum' on", // Optional feature settings

  color: '#303030',

}));

const ResultBoxContainer = styled(Box)(({ theme }) => ({
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  padding: "9px 20px 17px",
  gap: "10px",
  isolation: "isolate",
  background: "#FFFFFF",
  border: "4px solid #038C5A",
  borderRadius: "11px",
  zIndex: 2,
  marginTop: "-10rem"
}));

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",  // Applying the Inter font-family
  fontStyle: "normal",
  fontWeight: 700,
  fontSize: "19px",
  lineHeight: "23px", // Matches box height as requested
  textAlign: "center", // Center-aligns the text
  color: "#038C5A",
}));


const Subtitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif", // Applying the Inter font-family
  fontStyle: "normal",
  fontWeight: 700, // Bold text
  fontSize: "16px", // Font size 16px
  lineHeight: "19px", // Line height matches the box height
  color: "#303030", // Dark gray color
}));


const StyledList = styled(List)(({ theme }) => ({
  paddingLeft: "20px",
  counterReset: "list-counter",
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif", // Applying the Inter font-family
  fontStyle: "normal",
  fontWeight: 400, // Bold text
  fontSize: "16px", // Font size 16px
  lineHeight: "14px", // Line height matches the box height
  color: "#303030",
  counterIncrement: "list-counter",
  "&::before": {
    content: "counter(list-counter) '.'", // Display the counter value with a dot
    position: "absolute",
    left: 0,
    fontWeight: 700, // Bold numbers
    color: "#000", // Green color for numbers
  },
}));

const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: "underline",
  color: "#000",
  marginRight: '2px',
  marginLeft: '2px'
}));
const MotionWrapper = styled(motion.div)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',

  backgroundColor: theme.backgroundColor || 'transparent',
}));


const WizardContainer = styled(Box)(({ theme }) => ({
  height: "100%",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    padding: "16px 20px",
  },
}));

const WizardContent = styled(Box)(({ theme }) => ({
  display: "flex",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: 0,
  },
}));

const VerticalLine = styled(Box)(({ theme }) => ({
  width: "4px",
  height: "87px",
  backgroundColor: "#303030",
  margin: "0px auto 35px",
}));

const TopResultBox = styled(Box)(({ theme }) => ({
  color: "#fff",
  backgroundColor: "#025131",
  padding: "5px",
  borderRadius: "4px",
  width: "250px",
  height: "120px",
  boxSizing: "border-box",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  fontWeight: 700,
  fontSize: "20px",
  lineHeight: "24px",
  fontFamily: "'Inter', sans-serif",
  cursor: "not-allowed",
  pointerEvents: "none",
}));


const FirstSide = styled(Box)(({ theme }) => ({



}));

const StyledBackButton = styled(Button)(({ theme }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '5px 5px',
  gap: '5px',
  width: '110px',
  fontFamily: 'Inter, sans-serif',
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '20px',
  lineHeight: '24px',
  backgroundColor: ACCELIDEA_DARK_GREEN,
  border: '1px solid #303030',
  borderRadius: '4px',
  color: 'white',
  fontSize: '16px',
  cursor: 'pointer',
  textTransform: "capitalize",

  '&:hover': {
    backgroundColor: '#024428',
  },
}));

const MainColumn = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
}));

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  maxWidth: "100%",
  flexDirection: "column",
  padding: "30px 36px 0px",
  fontFamily: "Inter, sans-serif",
  fontWeight: 700,
  fontSize: "20px",
  [theme.breakpoints.down("md")]: {
    marginLeft: "10px",
  },
}));

const WizardTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontStyle: "normal",
  fontWeight: 700,
  fontSize: "24px",
  lineHeight: "24px",
  display: "flex",
  alignItems: "center",
  color: ACCELIDEA_MID_GREEN,
  textDecoration: "none",
  marginTop: "13px",
}));

const TreeSection = styled(Box)(({ theme }) => ({
  marginTop: "28px",
  display: "flex",
  flexDirection: "column",
  minHeight: '130%',

  alignItems: "center",
  maxWidth: "100%",
  [theme.breakpoints.down("md")]: {
    marginTop: "40px",
  },
}));

const SideColumn = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: 'center',
  height: '100%',
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
}));

export default DecisionTreeWizard;
